import React from 'react';
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Alert,
    Box,
    Button,
    Chip,
    CircularProgress,
    Collapse,
    Link,
    List,
    ListItem,
    ListItemText,
    Typography,
} from '@mui/material';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import {
    AutoAwesome,
    ExpandLess,
    ExpandMore,
    GavelOutlined,
    ManageSearch,
    Medication,
    MenuBookOutlined,
    Science,
    Warning,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { CitationPopup } from '../common/CitationPopup';
import { RagCitation, SuggestionDeepDive, SuggestionSection, suggestionService } from '../../services/suggestionService';

interface DifferentialDiagnosis {
    name: string;
    details: string[];
}

interface AISuggestionsData {
    differential_diagnoses?: DifferentialDiagnosis[];
    recommended_tests_priority1?: string[];
    recommended_tests_priority2?: string[];
    warnings?: string[];
    treatment_suggestions?: string[];
    rag_citations?: RagCitation[];
    deep_dives?: Record<string, SuggestionDeepDive>;
}

interface AISuggestionsDisplayProps {
    consultationId: string;
    currentReport?: string;
    suggestions: unknown;
    rag_citations?: RagCitation[];
    onNavigateToDrug?: (drugId: string) => void;
    onSearchByMolecule?: (molecule: string) => void;
}

function parseSuggestions(suggestions: unknown): AISuggestionsData | null {
    if (!suggestions) return null;
    if (typeof suggestions === 'string') {
        try {
            return JSON.parse(suggestions) as AISuggestionsData;
        } catch {
            return null;
        }
    }
    return typeof suggestions === 'object' ? (suggestions as AISuggestionsData) : null;
}

type CitationKind = 'medication' | 'regulatory' | 'literature';

interface CitationPresentation {
    kind: CitationKind;
    color: string;
    borderColor: string;
    backgroundColor: string;
}

function getCitationPresentation(citation: RagCitation): CitationPresentation {
    const rawType = String(citation.metadata?.type || '').toLowerCase();
    if (rawType === 'medication' || rawType === 'drug' || rawType === 'rcp') {
        return {
            kind: 'medication',
            color: '#49BEFF',
            borderColor: '#B9E5FF',
            backgroundColor: '#E8F7FF',
        };
    }
    if (rawType === 'regulatory' || rawType === 'regulation') {
        return {
            kind: 'regulatory',
            color: '#4570EA',
            borderColor: '#B8CBFF',
            backgroundColor: '#F3F6FF',
        };
    }
    return {
        kind: 'literature',
        color: '#1976D2',
        borderColor: '#90CAF9',
        backgroundColor: '#ECF2FF',
    };
}

export function AISuggestionsDisplay({
    consultationId,
    currentReport,
    suggestions,
    rag_citations,
    onNavigateToDrug,
    onSearchByMolecule,
}: AISuggestionsDisplayProps) {
    const { t } = useTranslation();
    const translated = (key: string, defaultValue: string) => t(key, { defaultValue });
    // Detect if running in extension mode (iframe)
    const isExtensionMode = window.self !== window.top;
    const parsedSuggestions = React.useMemo(() => parseSuggestions(suggestions), [suggestions]);
    const citations = rag_citations || parsedSuggestions?.rag_citations;

    // State for popup
    const [anchorEl, setAnchorEl] = React.useState<HTMLElement | null>(null);
    const [activeCitation, setActiveCitation] = React.useState<RagCitation | null>(null);
    const [deepDives, setDeepDives] = React.useState<Record<string, SuggestionDeepDive>>(() => parsedSuggestions?.deep_dives || {});
    const [expandedDeepDives, setExpandedDeepDives] = React.useState<Record<string, boolean>>({});
    const [loadingDeepDives, setLoadingDeepDives] = React.useState<Record<string, boolean>>({});
    const [deepDiveErrors, setDeepDiveErrors] = React.useState<Record<string, boolean>>({});

    React.useEffect(() => {
        setDeepDives(parsedSuggestions?.deep_dives || {});
        setExpandedDeepDives({});
        setLoadingDeepDives({});
        setDeepDiveErrors({});
    }, [parsedSuggestions]);

    const handleCitationClick = (event: React.MouseEvent<HTMLElement>, citation: RagCitation) => {
        setAnchorEl(event.currentTarget);
        setActiveCitation(citation);
    };

    const handleClosePopup = () => {
        setAnchorEl(null);
        setActiveCitation(null);
    };

    const renderCitationCard = (citation: RagCitation, citationIndex: number, keyPrefix: string) => {
        const presentation = getCitationPresentation(citation);
        const title = String(
            citation.metadata?.title || citation.norm_source || citation.source || translated('consultation.source', 'Source')
        );
        const rawExcerpt = citation.content || citation.chunk_content || '';
        const excerpt = rawExcerpt
            .replace(/<[^>]*>/g, ' ')
            .replace(/\s+/g, ' ')
            .trim();
        const labels: Record<CitationKind, string> = {
            medication: translated('consultation.citationTypeMedication', 'RCP'),
            regulatory: translated('consultation.citationTypeRegulatory', 'Réglementation'),
            literature: translated('consultation.citationTypeLiterature', 'Publication'),
        };
        const icon =
            presentation.kind === 'medication' ? (
                <Medication fontSize="small" />
            ) : presentation.kind === 'regulatory' ? (
                <GavelOutlined fontSize="small" />
            ) : (
                <MenuBookOutlined fontSize="small" />
            );

        return (
            <Button
                key={`${keyPrefix}-citation-${citationIndex}`}
                data-citation-kind={presentation.kind}
                fullWidth
                variant="outlined"
                onClick={(event) => handleCitationClick(event, citation)}
                aria-label={`[${citationIndex + 1}] ${title}`}
                sx={{
                    width: '100%',
                    maxWidth: '100%',
                    minWidth: 0,
                    boxSizing: 'border-box',
                    overflow: 'hidden',
                    justifyContent: 'flex-start',
                    alignItems: 'stretch',
                    p: isExtensionMode ? 0.75 : 1,
                    color: presentation.color,
                    borderColor: presentation.borderColor,
                    bgcolor: presentation.backgroundColor,
                    textAlign: 'left',
                    textTransform: 'none',
                    '&:hover': {
                        borderColor: presentation.color,
                        bgcolor: presentation.backgroundColor,
                    },
                }}
            >
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: 1,
                        width: '100%',
                        maxWidth: '100%',
                        minWidth: 0,
                        boxSizing: 'border-box',
                        overflow: 'hidden',
                    }}
                >
                    <Box sx={{ display: 'flex', mt: 0.25, flexShrink: 0, color: presentation.color }}>{icon}</Box>
                    <Box sx={{ minWidth: 0, flex: 1, textAlign: 'left' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0 }}>
                            <Chip
                                label={labels[presentation.kind]}
                                size="small"
                                sx={{
                                    height: 20,
                                    flexShrink: 0,
                                    color: presentation.color,
                                    borderColor: presentation.borderColor,
                                    bgcolor: 'background.paper',
                                    fontSize: '0.6875rem',
                                    fontWeight: 700,
                                }}
                                variant="outlined"
                            />
                            <Typography
                                variant="body2"
                                component="span"
                                fontWeight={600}
                                sx={{
                                    minWidth: 0,
                                    overflow: 'hidden',
                                    overflowWrap: 'anywhere',
                                    whiteSpace: 'normal',
                                    display: '-webkit-box',
                                    WebkitBoxOrient: 'vertical',
                                    WebkitLineClamp: 2,
                                }}
                            >
                                [{citationIndex + 1}] {title}
                            </Typography>
                        </Box>
                        {excerpt && (
                            <Typography
                                variant="caption"
                                color="text.secondary"
                                component="span"
                                sx={{
                                    display: '-webkit-box',
                                    mt: 0.5,
                                    overflow: 'hidden',
                                    WebkitBoxOrient: 'vertical',
                                    WebkitLineClamp: 2,
                                    lineHeight: 1.35,
                                    overflowWrap: 'anywhere',
                                }}
                            >
                                {excerpt}
                            </Typography>
                        )}
                    </Box>
                </Box>
            </Button>
        );
    };

    // Helper to render markdown with citations
    const renderMarkdown = (content: string, markdownCitations = citations, blockParagraphs = false) => {
        if (!content) return null;

        // Convert [1], [2] style citations to markdown links: [1](#citation-1)
        // Also handle comma separated like [1, 2] -> [1](#citation-1), [2](#citation-2)
        const processedContent = content.replace(/\[(\d+(?:,\s*\d+)*)\]/g, (match, nums) => {
            const parts = nums.split(/,\s*/);
            return parts.map((n: string) => `[${n}](#citation-${n})`).join(', ');
        });

        return (
            <ReactMarkdown
                remarkPlugins={[remarkGfm]}
                components={{
                    p: ({ node: _node, ...props }) => (
                        <Typography
                            variant="body2"
                            component={blockParagraphs ? 'p' : 'span'}
                            sx={{
                                display: blockParagraphs ? 'block' : 'inline',
                                my: blockParagraphs ? 0.75 : 0,
                                wordWrap: 'break-word',
                                overflowWrap: 'break-word',
                                fontSize: isExtensionMode ? '0.8125rem' : '0.875rem',
                            }}
                        >
                            {props.children}
                        </Typography>
                    ),
                    ul: ({ node: _node, ...props }) => (
                        <Box component="ul" sx={{ pl: 2, my: 0.5, maxWidth: '100%', overflow: 'hidden' }}>
                            {props.children}
                        </Box>
                    ),
                    ol: ({ node: _node, ...props }) => (
                        <Box component="ol" sx={{ pl: 2, my: 0.5, maxWidth: '100%', overflow: 'hidden' }}>
                            {props.children}
                        </Box>
                    ),
                    li: ({ node: _node, ...props }) => (
                        <Box
                            component="li"
                            sx={{
                                typography: 'body2',
                                wordWrap: 'break-word',
                                overflowWrap: 'break-word',
                                fontSize: isExtensionMode ? '0.8125rem' : '0.875rem',
                            }}
                        >
                            {props.children}
                        </Box>
                    ),
                    table: ({ node: _node, ...props }) => (
                        <Box
                            sx={{
                                width: '100%',
                                maxWidth: '100%',
                                minWidth: 0,
                                my: 1.25,
                                overflow: 'hidden',
                                '& table': {
                                    width: '100%',
                                    maxWidth: '100%',
                                    borderCollapse: 'collapse',
                                    tableLayout: 'fixed',
                                    fontSize: isExtensionMode ? '0.6875rem' : '0.75rem',
                                },
                                '& th, & td': {
                                    p: isExtensionMode ? 0.5 : 0.75,
                                    border: '1px solid',
                                    borderColor: 'divider',
                                    textAlign: 'left',
                                    verticalAlign: 'top',
                                    overflowWrap: 'anywhere',
                                    wordBreak: 'break-word',
                                },
                                '& th': {
                                    bgcolor: 'primary.main',
                                    color: 'primary.contrastText',
                                    fontWeight: 700,
                                    lineHeight: 1.25,
                                },
                                '& td': {
                                    color: 'text.primary',
                                },
                                '& tbody tr:nth-of-type(even) td': {
                                    bgcolor: 'action.hover',
                                },
                                '& td p': {
                                    m: 0,
                                },
                            }}
                        >
                            <table {...props} />
                        </Box>
                    ),
                    a: ({ node: _node, ...props }) => {
                        if (props.href?.startsWith('#citation-')) {
                            const index = parseInt(props.href.replace('#citation-', '')) - 1;
                            const citation = markdownCitations?.[index];
                            if (citation) {
                                const presentation = getCitationPresentation(citation);
                                return (
                                    <Link
                                        component="button"
                                        onClick={(e) => handleCitationClick(e, citation)}
                                        sx={{
                                            fontSize: 'inherit',
                                            verticalAlign: 'baseline',
                                            textDecoration: 'none',
                                            fontWeight: 'bold',
                                            mx: 0.2,
                                            color: presentation.color,
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                            gap: 0.2,
                                        }}
                                    >
                                        [{index + 1}]{presentation.kind === 'medication' && <Medication sx={{ fontSize: 12 }} />}
                                        {presentation.kind === 'regulatory' && <GavelOutlined sx={{ fontSize: 12 }} />}
                                    </Link>
                                );
                            }
                            return <span style={{ color: 'gray' }}>[{index + 1}]</span>;
                        }
                        return <a {...props} target="_blank" rel="noopener noreferrer" />;
                    },
                }}
            >
                {processedContent}
            </ReactMarkdown>
        );
    };

    const handleDeepDive = async (section: SuggestionSection, itemIndex?: number) => {
        const key = `${section}:${itemIndex ?? 'all'}`;
        if (deepDives[key] && !deepDiveErrors[key]) {
            setExpandedDeepDives((previous) => ({ ...previous, [key]: !previous[key] }));
            return;
        }

        setExpandedDeepDives((previous) => ({ ...previous, [key]: true }));
        setLoadingDeepDives((previous) => ({ ...previous, [key]: true }));
        setDeepDiveErrors((previous) => ({ ...previous, [key]: false }));
        try {
            const response = await suggestionService.deepen({
                consultationId,
                section,
                itemIndex,
                currentReport,
            });
            setDeepDives((previous) => ({ ...previous, [key]: response.deep_dive }));
        } catch (error: unknown) {
            console.error('Failed to deepen AI suggestion:', error);
            setDeepDiveErrors((previous) => ({ ...previous, [key]: true }));
        } finally {
            setLoadingDeepDives((previous) => ({ ...previous, [key]: false }));
        }
    };

    const renderDeepDive = (section: SuggestionSection, itemIndex?: number) => {
        const key = `${section}:${itemIndex ?? 'all'}`;
        const deepDive = deepDives[key];
        const deepDiveCitations = deepDive?.rag_citations || [];
        const isExpanded = Boolean(expandedDeepDives[key]);
        const isLoading = Boolean(loadingDeepDives[key]);
        const hasError = Boolean(deepDiveErrors[key]);

        return (
            <Box sx={{ width: '100%', maxWidth: '100%', minWidth: 0, mt: 0.5, overflow: 'hidden' }}>
                <Button
                    size="small"
                    variant="text"
                    disabled={isLoading}
                    startIcon={isLoading ? <CircularProgress size={14} /> : <ManageSearch fontSize="small" />}
                    endIcon={deepDive && !isLoading ? isExpanded ? <ExpandLess /> : <ExpandMore /> : undefined}
                    onClick={() => void handleDeepDive(section, itemIndex)}
                    sx={{ textTransform: 'none', px: 0.5 }}
                >
                    {isLoading
                        ? translated('consultation.deepDiveLoading', 'Recherche en cours...')
                        : hasError
                          ? translated('consultation.deepDiveRetry', 'Réessayer')
                          : isExpanded && deepDive
                            ? translated('consultation.deepDiveCollapse', 'Réduire')
                            : translated('consultation.deepDiveAction', 'Approfondir')}
                </Button>

                <Collapse in={isExpanded} unmountOnExit>
                    <Box
                        sx={{
                            width: '100%',
                            maxWidth: '100%',
                            minWidth: 0,
                            mt: 0.5,
                            p: 1.5,
                            boxSizing: 'border-box',
                            overflow: 'hidden',
                            borderLeft: 3,
                            borderColor: 'primary.light',
                            bgcolor: 'action.hover',
                            borderRadius: '0 8px 8px 0',
                        }}
                    >
                        {hasError ? (
                            <Alert severity="error" sx={{ py: 0 }}>
                                {translated(
                                    'consultation.deepDiveError',
                                    "L'analyse approfondie n'a pas pu être générée. Réessayez dans un instant."
                                )}
                            </Alert>
                        ) : deepDive ? (
                            <>
                                <Typography variant="caption" color="primary.main" fontWeight="bold" display="block" sx={{ mb: 1 }}>
                                    {translated('consultation.deepDiveTitle', 'Analyse approfondie')}
                                </Typography>
                                <Box sx={{ minWidth: 0, maxWidth: '100%', overflow: 'hidden', '& > p': { mb: 1 } }}>
                                    {renderMarkdown(deepDive.content, deepDiveCitations, true)}
                                </Box>
                                {deepDiveCitations.length > 0 && (
                                    <Box sx={{ mt: 1.5, minWidth: 0, maxWidth: '100%', overflow: 'hidden' }}>
                                        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                                            {translated('consultation.deepDiveSources', 'Sources de cette analyse')}
                                        </Typography>
                                        <Box
                                            sx={{
                                                display: 'grid',
                                                gridTemplateColumns: 'minmax(0, 1fr)',
                                                gap: 0.75,
                                                width: '100%',
                                                maxWidth: '100%',
                                                minWidth: 0,
                                                overflow: 'hidden',
                                            }}
                                        >
                                            {deepDiveCitations.map((citation, citationIndex) =>
                                                renderCitationCard(citation, citationIndex, key)
                                            )}
                                        </Box>
                                    </Box>
                                )}
                            </>
                        ) : null}
                    </Box>
                </Collapse>
            </Box>
        );
    };

    if (!suggestions) return null;
    if (!parsedSuggestions && typeof suggestions === 'string') {
        return (
            <Box sx={{ p: 2, bgcolor: 'background.paper', borderRadius: 1 }}>
                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', fontFamily: 'monospace' }}>
                    {suggestions}
                </Typography>
            </Box>
        );
    }
    if (!parsedSuggestions) return null;

    return (
        <Box
            sx={{
                maxWidth: '100%',
                overflow: 'hidden',
                wordWrap: 'break-word',
                overflowWrap: 'break-word',
                ...(isExtensionMode && {
                    '& .MuiAccordionDetails-root': {
                        px: 0.75,
                    },
                    '& .MuiAccordionDetails-root > .MuiList-root > .MuiListItem-root': {
                        px: 0.25,
                    },
                }),
            }}
        >
            {/* Differential Diagnoses */}
            {parsedSuggestions.differential_diagnoses && parsedSuggestions.differential_diagnoses.length > 0 && (
                <Accordion defaultExpanded sx={{ maxWidth: '100%', overflow: 'hidden' }}>
                    <AccordionSummary expandIcon={<ExpandMore />}>
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1,
                                maxWidth: '100%',
                                overflow: 'hidden',
                            }}
                        >
                            <Science color="primary" sx={{ flexShrink: 0 }} />
                            <Typography
                                variant="subtitle2"
                                fontWeight="bold"
                                sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                            >
                                {translated('consultation.differentialDiagnoses', 'Diagnostics différentiels')}
                            </Typography>
                        </Box>
                    </AccordionSummary>
                    <AccordionDetails sx={{ maxWidth: '100%', overflow: 'hidden' }}>
                        <List dense>
                            {parsedSuggestions.differential_diagnoses.map((diagnosis, idx) => (
                                <ListItem key={idx} sx={{ flexDirection: 'column', alignItems: 'flex-start', mb: 1 }}>
                                    <Box sx={{ width: '100%', mb: 0.5 }}>
                                        <Typography variant="body2" component="div" fontWeight="bold" color="primary.main">
                                            {renderMarkdown(diagnosis.name)}
                                        </Typography>
                                    </Box>
                                    {diagnosis.details && diagnosis.details.length > 0 && (
                                        <List dense sx={{ pl: 2 }}>
                                            {diagnosis.details.map((detail, detailIdx) => (
                                                <ListItem key={detailIdx} sx={{ py: 0.5 }}>
                                                    <Typography variant="body2" color="text.secondary" component="div">
                                                        <Box component="span" sx={{ mr: 1 }}>
                                                            •
                                                        </Box>
                                                        {renderMarkdown(detail)}
                                                    </Typography>
                                                </ListItem>
                                            ))}
                                        </List>
                                    )}
                                    {renderDeepDive('differential_diagnoses', idx)}
                                </ListItem>
                            ))}
                        </List>
                    </AccordionDetails>
                </Accordion>
            )}

            {/* Recommended Tests - Priority 1 */}
            {parsedSuggestions.recommended_tests_priority1 && parsedSuggestions.recommended_tests_priority1.length > 0 && (
                <Accordion defaultExpanded sx={{ maxWidth: '100%', overflow: 'hidden' }}>
                    <AccordionSummary expandIcon={<ExpandMore />}>
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1,
                                maxWidth: '100%',
                                overflow: 'hidden',
                            }}
                        >
                            <Science color="error" sx={{ flexShrink: 0 }} />
                            <Typography
                                variant="subtitle2"
                                fontWeight="bold"
                                sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                            >
                                {translated('consultation.recommendedTestsPriority1', 'Examens complémentaires - Priorité 1')}
                            </Typography>
                        </Box>
                    </AccordionSummary>
                    <AccordionDetails sx={{ maxWidth: '100%', overflow: 'hidden' }}>
                        <List dense>
                            {parsedSuggestions.recommended_tests_priority1.map((test, idx) => (
                                <ListItem key={idx} sx={{ flexDirection: 'column', alignItems: 'flex-start' }}>
                                    <ListItemText
                                        primary={renderMarkdown(test)}
                                        primaryTypographyProps={{ variant: 'body2', component: 'div' }}
                                    />
                                </ListItem>
                            ))}
                        </List>
                        {renderDeepDive('recommended_tests_priority1')}
                    </AccordionDetails>
                </Accordion>
            )}

            {/* Recommended Tests - Priority 2 */}
            {parsedSuggestions.recommended_tests_priority2 && parsedSuggestions.recommended_tests_priority2.length > 0 && (
                <Accordion sx={{ maxWidth: '100%', overflow: 'hidden' }}>
                    <AccordionSummary expandIcon={<ExpandMore />}>
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1,
                                maxWidth: '100%',
                                overflow: 'hidden',
                            }}
                        >
                            <Science color="warning" sx={{ flexShrink: 0 }} />
                            <Typography
                                variant="subtitle2"
                                fontWeight="bold"
                                sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                            >
                                {translated('consultation.recommendedTestsPriority2', 'Examens complémentaires - Priorité 2')}
                            </Typography>
                        </Box>
                    </AccordionSummary>
                    <AccordionDetails sx={{ maxWidth: '100%', overflow: 'hidden' }}>
                        <List dense>
                            {parsedSuggestions.recommended_tests_priority2.map((test, idx) => (
                                <ListItem key={idx} sx={{ flexDirection: 'column', alignItems: 'flex-start' }}>
                                    <ListItemText
                                        primary={renderMarkdown(test)}
                                        primaryTypographyProps={{ variant: 'body2', component: 'div' }}
                                    />
                                </ListItem>
                            ))}
                        </List>
                        {renderDeepDive('recommended_tests_priority2')}
                    </AccordionDetails>
                </Accordion>
            )}

            {/* Warnings */}
            {parsedSuggestions.warnings && parsedSuggestions.warnings.length > 0 && (
                <Accordion defaultExpanded sx={{ maxWidth: '100%', overflow: 'hidden' }}>
                    <AccordionSummary expandIcon={<ExpandMore />}>
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1,
                                maxWidth: '100%',
                                overflow: 'hidden',
                            }}
                        >
                            <Warning color="warning" sx={{ flexShrink: 0 }} />
                            <Typography
                                variant="subtitle2"
                                fontWeight="bold"
                                sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                            >
                                {translated('consultation.warnings', 'Avertissements')}
                            </Typography>
                        </Box>
                    </AccordionSummary>
                    <AccordionDetails sx={{ maxWidth: '100%', overflow: 'hidden' }}>
                        <List dense>
                            {parsedSuggestions.warnings.map((warning, idx) => (
                                <ListItem key={idx} sx={{ flexDirection: 'column', alignItems: 'flex-start' }}>
                                    <ListItemText
                                        primary={renderMarkdown(warning)}
                                        primaryTypographyProps={{
                                            variant: 'body2',
                                            color: 'warning.main',
                                            component: 'div',
                                        }}
                                    />
                                </ListItem>
                            ))}
                        </List>
                        {renderDeepDive('warnings')}
                    </AccordionDetails>
                </Accordion>
            )}

            {/* Treatment Suggestions */}
            {parsedSuggestions.treatment_suggestions && parsedSuggestions.treatment_suggestions.length > 0 && (
                <Accordion defaultExpanded sx={{ maxWidth: '100%', overflow: 'hidden' }}>
                    <AccordionSummary expandIcon={<ExpandMore />}>
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1,
                                maxWidth: '100%',
                                overflow: 'hidden',
                            }}
                        >
                            <Medication color="success" sx={{ flexShrink: 0 }} />
                            <Typography
                                variant="subtitle2"
                                fontWeight="bold"
                                sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                            >
                                {translated('consultation.treatmentSuggestions', 'Suggestions de traitement')}
                            </Typography>
                        </Box>
                    </AccordionSummary>
                    <AccordionDetails sx={{ maxWidth: '100%', overflow: 'hidden' }}>
                        <List dense>
                            {parsedSuggestions.treatment_suggestions.map((treatment, idx) => (
                                <ListItem key={idx} sx={{ flexDirection: 'column', alignItems: 'flex-start' }}>
                                    <ListItemText
                                        primary={renderMarkdown(treatment)}
                                        primaryTypographyProps={{ variant: 'body2', component: 'div' }}
                                    />
                                </ListItem>
                            ))}
                        </List>
                        {renderDeepDive('treatment_suggestions')}
                    </AccordionDetails>
                </Accordion>
            )}

            {/* RAG Citations / References */}
            {citations && citations.length > 0 && (
                <Accordion defaultExpanded sx={{ maxWidth: '100%', overflow: 'hidden' }}>
                    <AccordionSummary expandIcon={<ExpandMore />}>
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1,
                                maxWidth: '100%',
                                overflow: 'hidden',
                            }}
                        >
                            <AutoAwesome color="info" sx={{ flexShrink: 0 }} />
                            <Typography
                                variant="subtitle2"
                                fontWeight="bold"
                                sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                            >
                                {translated('consultation.referencesAndMedications', 'Références et médicaments')}
                            </Typography>
                        </Box>
                    </AccordionSummary>
                    <AccordionDetails sx={{ maxWidth: '100%', overflow: 'hidden' }}>
                        <List
                            dense
                            disablePadding
                            sx={{
                                display: 'grid',
                                gridTemplateColumns: 'minmax(0, 1fr)',
                                gap: 0.75,
                                width: '100%',
                                maxWidth: '100%',
                                minWidth: 0,
                                overflow: 'hidden',
                            }}
                        >
                            {citations.map((citation, idx) => (
                                <ListItem key={idx} disablePadding sx={{ width: '100%', maxWidth: '100%', minWidth: 0 }}>
                                    {renderCitationCard(citation, idx, 'suggestions')}
                                </ListItem>
                            ))}
                        </List>
                    </AccordionDetails>
                </Accordion>
            )}

            {/* Citation Popup */}
            <CitationPopup
                citation={activeCitation}
                anchorEl={anchorEl}
                onClose={handleClosePopup}
                onNavigateToDrug={onNavigateToDrug}
                onSearchByMolecule={onSearchByMolecule}
            />
        </Box>
    );
}
